import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, UserPaginatedResponse } from "../../types/users";

interface UsersState {
  users: User[];
  loading: boolean;
  error: string | null;
  addUserLoading: boolean;
  addUserSuccess: boolean;
  addUserError: string | null;
  editUserLoading: boolean;
  editUserSuccess: boolean;
  editUserError: string | null;
  deleteUserLoading: boolean;
  deleteUserSuccess: boolean;
  deleteUserError: string | null;
  selectedUser: User | null;
  fetchUserLoading: boolean;
  fetchUserError: string | null;
  resendEmailLoading: boolean;
  resendEmailError: string | null;
  resendEmailSuccess: boolean;
  profile: User | null;
  profileLoading: boolean;
  profileError: string | null;
  changePasswordLoading: boolean;
  changePasswordSuccess: boolean;
  changePasswordError: string | null;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: number;
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
  addUserLoading: false,
  addUserError: null,
  addUserSuccess: false,
  editUserLoading: false,
  editUserError: null,
  editUserSuccess: false,
  deleteUserLoading: false,
  deleteUserError: null,
  deleteUserSuccess: false,
  selectedUser: null,
  fetchUserLoading: false,
  fetchUserError: null,
  resendEmailLoading: false,
  resendEmailError: null,
  resendEmailSuccess: false,
  profile: null,
  profileLoading: false,
  profileError: null,
  changePasswordLoading: false,
  changePasswordSuccess: false,
  changePasswordError: null,
  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  sortBy: "email",
  sortOrder: -1,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    fetchUsers: (
      state,
      action: PayloadAction<{ page: number; rowsPerPage: number; sortBy?: string; sortOrder?: number }>
    ) => { },
    fetchUsersStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUsersSuccess: (
      state,
      action: PayloadAction<UserPaginatedResponse>
    ) => {
      state.loading = false;
      state.users = action.payload.data;
      state.totalCount = action.payload.total;
    },
    fetchUsersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.page = 0;
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.sortOrder = action.payload;
      state.page = 0;
    },
    addUser: (
      state,
      action: PayloadAction<
        Omit<User, "id" | "created_at" | "updated_at" | "registered_at">
      >
    ) => { },
    addUserStart: (state) => {
      state.addUserLoading = true;
      state.addUserError = null;
      state.addUserSuccess = false;
    },
    addUserSuccess: (state, action: PayloadAction<User>) => {
      state.addUserLoading = false;
      state.users.push(action.payload);
      state.addUserSuccess = true;
    },
    addUserFailure: (state, action: PayloadAction<string>) => {
      state.addUserLoading = false;
      state.addUserError = action.payload;
      state.addUserSuccess = false;
    },
    editUser: (
      state,
      action: PayloadAction<{
        userId: string;
        userData: Partial<
          Omit<
            User,
            | "id"
            | "created_at"
            | "updated_at"
            | "registered_at"
            | "email"
            | "verified_email"
          >
        >;
      }>
    ) => { },
    editUserStart: (state) => {
      state.editUserLoading = true;
      state.editUserError = null;
      state.editUserSuccess = false;
    },
    editUserSuccess: (state, action: PayloadAction<User>) => {
      state.editUserLoading = false;
      const index = state.users.findIndex(
        (user) => user.id === action.payload.id
      );
      if (index !== -1) {
        state.users[index] = action.payload;
      }
      state.editUserSuccess = true;
    },
    editUserFailure: (state, action: PayloadAction<string>) => {
      state.editUserLoading = false;
      state.editUserError = action.payload;
      state.editUserSuccess = false;
    },
    deleteUser: (state, action: PayloadAction<string>) => { },
    deleteUserStart: (state) => {
      state.deleteUserLoading = true;
      state.deleteUserError = null;
      state.deleteUserSuccess = false;
    },
    deleteUserSuccess: (state, action: PayloadAction<string>) => {
      state.deleteUserLoading = false;
      state.users = state.users.filter((user) => user.id !== action.payload);
      state.deleteUserSuccess = true;
    },
    deleteUserFailure: (state, action: PayloadAction<string>) => {
      state.deleteUserLoading = false;
      state.deleteUserError = action.payload;
      state.deleteUserSuccess = false;
    },
    fetchUserById: (state, action: PayloadAction<string>) => { },
    fetchUserByIdStart: (state) => {
      state.fetchUserLoading = true;
      state.fetchUserError = null;
      state.selectedUser = null;
    },
    fetchUserByIdSuccess: (state, action: PayloadAction<User>) => {
      state.fetchUserLoading = false;
      state.selectedUser = action.payload;
    },
    fetchUserByIdFailure: (state, action: PayloadAction<string>) => {
      state.fetchUserLoading = false;
      state.fetchUserError = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
      state.fetchUserError = null;
    },
    clearUsersError: (state) => {
      state.error = null;
      state.addUserError = null;
      state.addUserSuccess = false;
      state.editUserError = null;
      state.editUserSuccess = false;
      state.deleteUserError = null;
      state.deleteUserSuccess = false;
      state.fetchUserError = null;
    },
    // Resend email
    resendEmail: (state, action: PayloadAction<string>) => { },
    resendEmailStart: (state) => {
      state.resendEmailLoading = true;
      state.resendEmailError = null;
      state.resendEmailSuccess = false;
    },
    resendEmailSuccess: (state) => {
      state.resendEmailLoading = false;
      state.resendEmailSuccess = true;
    },
    resendEmailFailure: (state, action: PayloadAction<string>) => {
      state.resendEmailLoading = false;
      state.resendEmailError = action.payload;
      state.resendEmailSuccess = false;
    },
    changePassword: (
      state,
      action: PayloadAction<{ old_password: string; new_password: string }>
    ) => { },
    changePasswordStart: (state) => {
      state.changePasswordLoading = true;
      state.changePasswordError = null;
      state.changePasswordSuccess = false;
    },
    changePasswordSuccess: (state) => {
      state.changePasswordLoading = false;
      state.changePasswordSuccess = true;
    },
    changePasswordFailure: (state, action: PayloadAction<string>) => {
      state.changePasswordLoading = false;
      state.changePasswordError = action.payload;
      state.changePasswordSuccess = false;
    },

    fetchProfile: (state) => { },
    fetchProfileStart: (state) => {
      state.profileLoading = true;
      state.profileError = null;
    },
    fetchProfileSuccess: (state, action: PayloadAction<User>) => {
      state.profileLoading = false;
      state.profile = action.payload;
    },
    fetchProfileFailure: (state, action: PayloadAction<string>) => {
      state.profileLoading = false;
      state.profileError = action.payload;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.profileError = null;
    },

    clearChangePasswordError: (state) => {
      state.changePasswordError = null;
    },

    clearReverificationError: (state) => {
      state.resendEmailLoading = false;
      state.resendEmailError = null;
      state.resendEmailSuccess = false;
    },
  },
});

export const {
  fetchUsers,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  setPage,
  setRowsPerPage,
  setSortBy,
  setSortOrder,
  addUser,
  addUserStart,
  addUserSuccess,
  addUserFailure,
  editUser,
  editUserStart,
  editUserSuccess,
  editUserFailure,
  deleteUser,
  deleteUserStart,
  deleteUserSuccess,
  deleteUserFailure,
  resendEmail,
  resendEmailStart,
  resendEmailSuccess,
  resendEmailFailure,
  fetchUserById,
  fetchUserByIdStart,
  fetchUserByIdSuccess,
  fetchUserByIdFailure,
  clearSelectedUser,
  clearUsersError,
  fetchProfile,
  fetchProfileStart,
  fetchProfileSuccess,
  fetchProfileFailure,
  clearProfile,
  changePassword,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailure,
  clearChangePasswordError,
  clearReverificationError
} = usersSlice.actions;
export default usersSlice.reducer;
